import { Protocol } from '../../../models/Entities/Protocols/Protocol';
import {
    AgingBucketStatisticViewModel,
    CashflowSeriesPointViewModel,
    DebtSeriesPointViewModel,
    MonthlyCashflowStatisticViewModel,
    OverviewStatisticViewModel,
    PaymentDelayStatisticViewModel
} from '../../../models/Entities/CashFlow/Statistics/StatisticsModels';

/**
 * Panel financiero del centro: cálculos.
 *
 * Todo lo que sale en pantalla se arma acá, con funciones puras que se prueban
 * solas. La regla que manda sobre todas: **cada moneda se mira por separado**. La
 * API devuelve una fila por moneda y sumar pesos con dólares sin tipo de cambio de
 * cierre da un número que no significa nada.
 *
 * El panel contesta tres preguntas, en este orden:
 *   1. Caja: ¿tengo la plata o me la deben?
 *   2. Rentabilidad: ¿qué protocolo deja plata y cuál la drena?
 *   3. Proyección: ¿en qué mes me quedo corto?
 */

//#region Formato

export const formatMoney = (value: number, currency: string): string => {
    try {
        return new Intl.NumberFormat('es-AR', {
            style: 'currency',
            currency: currency || 'USD',
            maximumFractionDigits: 0
        }).format(value);
    } catch {
        return value.toLocaleString('es-AR', { maximumFractionDigits: 0 });
    }
};

export const formatCompactMoney = (value: number, currency: string): string => {
    try {
        return new Intl.NumberFormat('es-AR', {
            style: 'currency',
            currency: currency || 'USD',
            notation: 'compact',
            maximumFractionDigits: 1
        }).format(value);
    } catch {
        return value.toLocaleString('es-AR', { maximumFractionDigits: 0 });
    }
};

export const formatPercent = (value: number, decimals = 1): string => `${value.toFixed(decimals)}%`;

export const formatDays = (value: number): string =>
    `${Math.round(value)} ${Math.round(value) === 1 ? 'día' : 'días'}`;

/** Porcentaje de una parte sobre un total, con 1 decimal; 0 si no hay contra qué comparar. */
export const share = (part: number, total: number): number =>
    total > 0 ? Math.round((part / total) * 1000) / 10 : 0;

//#endregion Formato

//#region Fechas y monedas

export const startOfToday = (): Date => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
};

export const monthsBack = (months: number, from: Date = startOfToday()): Date => {
    const date = new Date(from);
    date.setMonth(date.getMonth() - months);
    return date;
};

export const isoDate = (date?: Date): string | undefined => (date ? date.toISOString().slice(0, 10) : undefined);

const MONTH_NAMES = [
    'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
    'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'
];

/** "mar 2026", para los ejes de los gráficos. */
export const formatMonthLabel = (year: number, month: number): string =>
    `${MONTH_NAMES[month - 1].slice(0, 3)} ${year}`;

export const addMonths = (date: Date, months: number): Date => {
    const result = new Date(date.getFullYear(), date.getMonth() + months, 1);
    return result;
};

/** Monedas presentes en cualquiera de las respuestas, sin repetir y en orden estable. */
export const availableCurrencies = (
    ...sources: { currencyCode?: string }[][]
): string[] => {
    const seen = new Set<string>();
    sources.forEach((source) =>
        (source ?? []).forEach((item) => {
            if (item?.currencyCode) seen.add(item.currencyCode);
        })
    );
    return Array.from(seen).sort((a, b) => a.localeCompare(b));
};

/** La moneda elegida si sigue estando; si no, la primera disponible. */
export const pickCurrency = (currencies: string[], current?: string): string | undefined => {
    if (current && currencies.includes(current)) return current;
    return currencies[0];
};

//#endregion Fechas y monedas

//#region Bloque 1 — Caja

export interface CashHealth {
    currency: string;
    /** Cobrado en el período. */
    collected: number;
    /** Deuda generada y su estado. */
    debtOriginal: number;
    debtPaid: number;
    debtPending: number;
    debtCount: number;
    settledDebtCount: number;
    pendingDebtCount: number;
    /** Cobrado menos deuda pendiente. */
    netPosition: number;
    /** Pagos recibidos que todavía no se imputaron a ninguna deuda. */
    unimputed: number;
    /** Días promedio de cobro (DSO) y su rango. */
    averageDelayDays?: number;
    minDelayDays?: number;
    maxDelayDays?: number;
    /** Deuda pendiente con más de 90 días y cuánto pesa sobre el total pendiente. */
    overdueAmount: number;
    overdueShare: number;
    /** Porcentaje de la deuda generada que ya se cobró. */
    recoveryRate: number;
}

/** Tramo de antigüedad listo para mostrar, ordenado del más nuevo al más viejo. */
export interface AgingRow {
    bucket: string;
    label: string;
    fromDays: number;
    toDays: number | null;
    amount: number;
    debtCount: number;
    share: number;
    /** Un tramo de más de 60 días ya es plata en riesgo. */
    isRisk: boolean;
}

const RISK_FROM_DAYS = 61;

export const agingRows = (aging: AgingBucketStatisticViewModel[], currency: string): AgingRow[] => {
    const rows = (aging ?? []).filter((item) => item.currencyCode === currency);
    const total = rows.reduce((acc, item) => acc + (item.totalPendingAmount || 0), 0);

    return rows
        .slice()
        .sort((a, b) => (a.fromDays ?? 0) - (b.fromDays ?? 0))
        .map((item) => ({
            bucket: item.bucket,
            label: item.toDays === null || item.toDays === undefined
                ? `Más de ${item.fromDays} días`
                : `${item.fromDays} a ${item.toDays} días`,
            fromDays: item.fromDays ?? 0,
            toDays: item.toDays ?? null,
            amount: item.totalPendingAmount || 0,
            debtCount: item.debtCount || 0,
            share: share(item.totalPendingAmount || 0, total),
            isRisk: (item.fromDays ?? 0) >= RISK_FROM_DAYS
        }));
};

export const buildCashHealth = (
    overview: OverviewStatisticViewModel | undefined,
    delay: PaymentDelayStatisticViewModel | undefined,
    aging: AgingRow[],
    currency: string
): CashHealth => {
    const pendingTotal = aging.reduce((acc, row) => acc + row.amount, 0);
    const overdueAmount = aging.filter((row) => row.toDays === null).reduce((acc, row) => acc + row.amount, 0);
    const debtOriginal = overview?.totalDebtOriginalAmount ?? 0;

    return {
        currency,
        collected: overview?.totalPaymentAmount ?? 0,
        debtOriginal,
        debtPaid: overview?.totalDebtPaidAmount ?? 0,
        debtPending: overview?.totalDebtPendingAmount ?? 0,
        debtCount: overview?.debtCount ?? 0,
        settledDebtCount: overview?.settledDebtCount ?? 0,
        pendingDebtCount: overview?.pendingDebtCount ?? 0,
        netPosition: overview?.netPosition ?? 0,
        unimputed: overview?.totalPaymentPendingImputationAmount ?? 0,
        averageDelayDays: delay?.averageDelayDays,
        minDelayDays: delay?.minDelayDays,
        maxDelayDays: delay?.maxDelayDays,
        overdueAmount,
        overdueShare: share(overdueAmount, pendingTotal || overview?.totalDebtPendingAmount || 0),
        recoveryRate: share(overview?.totalDebtPaidAmount ?? 0, debtOriginal)
    };
};

/** Tasa de recupero promedio de la serie de deuda: cuánto de lo que se genera se cobra. */
export const averageSettlementRate = (series: DebtSeriesPointViewModel[]): number | undefined => {
    const points = (series ?? []).filter((point) => typeof point.settlementRate === 'number');
    if (points.length === 0) return undefined;
    const total = points.reduce((acc, point) => acc + point.settlementRate, 0);
    return Math.round((total / points.length) * 10) / 10;
};

//#endregion Bloque 1 — Caja

//#region Bloque 2 — Rentabilidad

export interface ProtocolCashflow {
    protocol: Protocol;
    rows: MonthlyCashflowStatisticViewModel[];
}

export interface ProfitabilityRow {
    protocolId: number;
    protocolNumber: string;
    name: string;
    sponsor: string;
    /** Ingresos y egresos de caja del protocolo en el período. */
    revenue: number;
    cost: number;
    profit: number;
    /** Resultado sobre ingresos. */
    margin: number;
    /** Resultado sobre costos: cuánto rinde cada peso puesto. */
    roi: number;
    /** Cuánto pesa este protocolo en los ingresos del centro. */
    revenueShare: number;
}

export type ProfitabilityOrder = 'profit' | 'margin' | 'roi' | 'revenue';

const protocolName = (protocol: Protocol): string => {
    const any = protocol as any;
    return any.title || any.fullTitle || any.protocolNumber || any.name || `#${protocol.id}`;
};

/** El sponsor viene como `laboratory`, y la API lo publica con la errata `laboratoy`. */
export const sponsorOf = (protocol: Protocol): string =>
    (protocol.laboratory?.title ?? (protocol as any).laboratoy?.title ?? '') || 'Sin sponsor';

/**
 * Rentabilidad por protocolo a partir del cashflow mensual de cada uno: los ingresos
 * son la plata que entró y los costos la que salió, en el período y la moneda
 * elegidos. Se descartan los protocolos sin movimiento: no aportan a la lectura.
 */
export const buildProfitabilityRows = (entries: ProtocolCashflow[]): ProfitabilityRow[] => {
    const rows = (entries ?? [])
        .map(({ protocol, rows: monthly }) => {
            const revenue = (monthly ?? []).reduce((acc, row) => acc + (row.income || 0), 0);
            const cost = (monthly ?? []).reduce((acc, row) => acc + (row.outcome || 0), 0);
            const profit = revenue - cost;
            return {
                protocolId: protocol.id,
                protocolNumber: protocol.protocolNumber || `#${protocol.id}`,
                name: protocolName(protocol),
                sponsor: sponsorOf(protocol),
                revenue,
                cost,
                profit,
                margin: revenue > 0 ? Math.round((profit / revenue) * 1000) / 10 : 0,
                roi: cost > 0 ? Math.round((profit / cost) * 1000) / 10 : 0,
                revenueShare: 0
            };
        })
        .filter((row) => row.revenue !== 0 || row.cost !== 0);

    const totalRevenue = rows.reduce((acc, row) => acc + row.revenue, 0);
    return rows.map((row) => ({ ...row, revenueShare: share(row.revenue, totalRevenue) }));
};

export const sortProfitability = (rows: ProfitabilityRow[], order: ProfitabilityOrder): ProfitabilityRow[] =>
    rows.slice().sort((a, b) => {
        if (order === 'margin') return a.margin - b.margin;
        if (order === 'roi') return a.roi - b.roi;
        if (order === 'revenue') return b.revenue - a.revenue;
        return a.profit - b.profit;
    });

export interface ProfitabilityTotals {
    revenue: number;
    cost: number;
    profit: number;
    margin: number;
    /** Protocolos con resultado negativo y cuánta plata se llevan. */
    losingCount: number;
    losingAmount: number;
}

export const profitabilityTotals = (rows: ProfitabilityRow[]): ProfitabilityTotals => {
    const revenue = rows.reduce((acc, row) => acc + row.revenue, 0);
    const cost = rows.reduce((acc, row) => acc + row.cost, 0);
    const profit = revenue - cost;
    const losing = rows.filter((row) => row.profit < 0);

    return {
        revenue,
        cost,
        profit,
        margin: revenue > 0 ? Math.round((profit / revenue) * 1000) / 10 : 0,
        losingCount: losing.length,
        losingAmount: losing.reduce((acc, row) => acc + row.profit, 0)
    };
};

export interface SponsorProfitability {
    sponsor: string;
    protocols: number;
    revenue: number;
    cost: number;
    profit: number;
    margin: number;
}

/** El mismo cálculo agrupado por sponsor: con quién conviene seguir trabajando. */
export const aggregateBySponsor = (rows: ProfitabilityRow[]): SponsorProfitability[] => {
    const map = new Map<string, SponsorProfitability>();

    rows.forEach((row) => {
        const current = map.get(row.sponsor) ?? {
            sponsor: row.sponsor,
            protocols: 0,
            revenue: 0,
            cost: 0,
            profit: 0,
            margin: 0
        };
        current.protocols += 1;
        current.revenue += row.revenue;
        current.cost += row.cost;
        current.profit += row.profit;
        map.set(row.sponsor, current);
    });

    return Array.from(map.values())
        .map((item) => ({
            ...item,
            margin: item.revenue > 0 ? Math.round((item.profit / item.revenue) * 1000) / 10 : 0
        }))
        .sort((a, b) => b.profit - a.profit);
};

export type MarginHealth = 'good' | 'warn' | 'bad';

/** Semáforo del margen contra el mínimo aceptable del centro. */
export const marginHealth = (margin: number, minMargin: number): MarginHealth => {
    if (margin < 0) return 'bad';
    return margin >= minMargin ? 'good' : 'warn';
};

//#endregion Bloque 2 — Rentabilidad

//#region Bloque 3 — Proyección

export interface CollectionSlot {
    /** 1 = el mes que viene. */
    monthIndex: number;
    amount: number;
}

export interface CollectionSchedule {
    slots: CollectionSlot[];
    /** Deuda que, al ritmo de cobro actual, cae después del horizonte proyectado. */
    beyondHorizon: number;
    total: number;
}

const DAYS_IN_MONTH = 30;

/**
 * Cuándo se espera cobrar la deuda que YA está generada.
 *
 * Regla, deliberadamente simple para poder explicarla en una reunión: el centro
 * cobra, en promedio, a los `dsoDays` días de generada la deuda. A cada tramo de
 * antigüedad le queda por esperar lo que le falta para llegar a ese promedio; lo
 * que ya lo pasó se espera para el mes que viene. No inventa ingresos nuevos: sólo
 * ubica en el tiempo plata que alguien ya debe.
 */
export const buildCollectionSchedule = (
    aging: AgingRow[],
    dsoDays: number | undefined,
    monthsAhead: number
): CollectionSchedule => {
    const dso = dsoDays && dsoDays > 0 ? dsoDays : DAYS_IN_MONTH;
    const slots = new Map<number, number>();
    let beyondHorizon = 0;
    let total = 0;

    (aging ?? []).forEach((row) => {
        if (row.amount === 0) return;
        total += row.amount;

        // Edad representativa del tramo: su piso, que es lo conservador.
        const remainingDays = Math.max(dso - row.fromDays, 0);
        const monthIndex = Math.max(1, Math.ceil(remainingDays / DAYS_IN_MONTH));

        if (monthIndex > monthsAhead) {
            beyondHorizon += row.amount;
            return;
        }
        slots.set(monthIndex, (slots.get(monthIndex) ?? 0) + row.amount);
    });

    return {
        slots: Array.from(slots.entries())
            .map(([monthIndex, amount]) => ({ monthIndex, amount }))
            .sort((a, b) => a.monthIndex - b.monthIndex),
        beyondHorizon,
        total
    };
};

export interface CashTrend {
    /** Promedio mensual de ingresos y egresos de los últimos meses con movimiento. */
    income: number;
    outcome: number;
    net: number;
    /** Cuántos meses se promediaron: si son pocos, la tendencia vale poco. */
    months: number;
}

/**
 * Tendencia: promedio de los últimos meses de la serie real. No extrapola
 * crecimiento ni estacionalidad; dice "si sigue como viene, esto entra y esto sale".
 */
export const buildTrend = (series: CashflowSeriesPointViewModel[], windowMonths = 6): CashTrend => {
    const points = (series ?? []).slice(-windowMonths);
    if (points.length === 0) return { income: 0, outcome: 0, net: 0, months: 0 };

    const income = points.reduce((acc, point) => acc + (point.income || 0), 0) / points.length;
    const outcome = points.reduce((acc, point) => acc + (point.outcome || 0), 0) / points.length;

    return {
        income: Math.round(income),
        outcome: Math.round(outcome),
        net: Math.round(income - outcome),
        months: points.length
    };
};

export interface ProjectionPoint {
    monthIndex: number;
    label: string;
    /** Cobranza de deuda ya generada que cae en el mes. */
    committedIncome: number;
    /** Ingresos si el centro sigue facturando como en los últimos meses. */
    trendIncome: number;
    /** Egresos esperados: los costos no paran. */
    expectedOutcome: number;
    netCommitted: number;
    netTrend: number;
    cumulativeCommitted: number;
    cumulativeTrend: number;
    /** El acumulado del escenario conservador quedó en rojo. */
    isShort: boolean;
}

export interface Projection {
    points: ProjectionPoint[];
    /** Primer mes en rojo del escenario conservador, si lo hay. */
    firstShortMonth?: ProjectionPoint;
    schedule: CollectionSchedule;
    trend: CashTrend;
    monthsAhead: number;
}

/**
 * Proyección en dos escenarios, nunca sumados entre sí:
 *
 * - **Comprometido**: entra sólo lo que ya te deben, cuando se espera cobrarlo, y
 *   salen los costos al ritmo actual. Es el piso, y es el que enciende la alarma.
 * - **Tendencia**: entra y sale lo mismo que viene entrando y saliendo.
 *
 * Arranca de cero: proyecta el flujo del período, no el saldo bancario, que el
 * sistema de cobranzas no conoce.
 */
export const buildProjection = (
    aging: AgingRow[],
    dsoDays: number | undefined,
    series: CashflowSeriesPointViewModel[],
    monthsAhead = 6,
    from: Date = startOfToday()
): Projection => {
    const schedule = buildCollectionSchedule(aging, dsoDays, monthsAhead);
    const trend = buildTrend(series);

    let cumulativeCommitted = 0;
    let cumulativeTrend = 0;

    const points: ProjectionPoint[] = [];
    for (let monthIndex = 1; monthIndex <= monthsAhead; monthIndex += 1) {
        const date = addMonths(from, monthIndex);
        const committedIncome = schedule.slots.find((slot) => slot.monthIndex === monthIndex)?.amount ?? 0;
        const netCommitted = committedIncome - trend.outcome;
        const netTrend = trend.income - trend.outcome;
        cumulativeCommitted += netCommitted;
        cumulativeTrend += netTrend;

        points.push({
            monthIndex,
            label: formatMonthLabel(date.getFullYear(), date.getMonth() + 1),
            committedIncome,
            trendIncome: trend.income,
            expectedOutcome: trend.outcome,
            netCommitted,
            netTrend,
            cumulativeCommitted,
            cumulativeTrend,
            isShort: cumulativeCommitted < 0
        });
    }

    return {
        points,
        firstShortMonth: points.find((point) => point.isShort),
        schedule,
        trend,
        monthsAhead
    };
};

//#endregion Bloque 3 — Proyección
